import z from "zod";
import type { AgentServer } from "@/agent/server/class";
import type { LifeErrorUnion } from "@/shared/error";
import type * as op from "@/shared/operation";
import type { Any, MaybePromise, Without } from "@/shared/types";
import type { ZodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import type { TelemetrySpanHandle } from "@/telemetry/types";
import type { PluginBuilder } from "./builder";

// Dependencies
export type PluginDependenciesDefinition = Without<PluginDefinition, "dependencies">[];

// Config
export type PluginConfigDefinition = ZodObjectWithTelemetry<z.ZodObject, "output">;

export type PluginConfigInput<ConfigDef extends PluginConfigDefinition> = z.input<
  ConfigDef["schema"]
>;
export type PluginConfig<
  ConfigDef extends PluginConfigDefinition,
  T extends "input" | "output",
> = T extends "input" ? z.input<ConfigDef["schema"]> : z.output<ConfigDef["schema"]>;

// Context
export type PluginContextDefinition = ZodObjectWithTelemetry<z.ZodObject, "output">;

export type PluginContext<
  ContextDef extends PluginContextDefinition,
  T extends "input" | "output",
> = T extends "input" ? z.input<ContextDef["schema"]> : z.output<ContextDef["schema"]>;

// Events
export const eventSourceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("handler"),
    plugin: z.string(),
    handler: z.string(),
    event: z.string(),
  }),
  z.object({
    type: z.literal("server"),
    name: z.string(),
  }),
  z.object({
    type: z.literal("client"),
    name: z.string(),
  }),
]);

export type PluginEventSource = z.infer<typeof eventSourceSchema>;

export const pluginEventSchema = z.object({
  id: z.string(),
  name: z.string(),
  urgent: z.boolean().prefault(false),
  data: z.any().prefault(null),
  created: z.object({
    at: z.number(),
    by: eventSourceSchema,
  }),
  edited: z
    .array(
      z.object({
        at: z.number(),
        by: z.object({
          plugin: z.string(),
          handler: z.string(),
        }),
        reason: z.string(),
        dataBefore: z.any(),
        dataAfter: z.any(),
      }),
    )
    .or(z.literal(false))
    .default(false),
  dropped: z
    .object({
      at: z.number(),
      by: z.object({
        plugin: z.string(),
        handler: z.string(),
      }),
      reason: z.string(),
    })
    .or(z.literal(false))
    .prefault(false),
  contextChanges: z
    .array(
      z.object({
        at: z.number(),
        byHandler: z.string(),
        valueBefore: z.any(),
        valueAfter: z.any(),
      }),
    )
    .prefault([]),
});

export const pluginEventInputSchema = pluginEventSchema.omit({
  id: true,
  created: true,
  edited: true,
  dropped: true,
  contextChanges: true,
});

export type PluginEventDataSchema = z.ZodObject | z.ZodDiscriminatedUnion<z.ZodObject[]>;

export type PluginEventDefinition<Name extends string> = {
  name: Name;
  dataSchema?: PluginEventDataSchema;
};

export type PluginEventsDefinition<Names extends string[] = string[]> = {
  [K in keyof Names]: PluginEventDefinition<Names[K]>;
};

export type PluginEvent<EventsDef extends PluginEventsDefinition, T extends "input" | "output"> = {
  [K in keyof EventsDef]: Omit<
    T extends "input"
      ? Omit<
          z.input<typeof pluginEventSchema>,
          "created" | "edited" | "dropped" | "id" | "contextChanges"
        >
      : z.output<typeof pluginEventSchema>,
    "data" | "name"
  > & {
    name: EventsDef[K]["name"];
  } & (EventsDef[K]["dataSchema"] extends PluginEventDataSchema
      ? T extends "input"
        ? { data: z.input<EventsDef[K]["dataSchema"]> }
        : { data: z.output<EventsDef[K]["dataSchema"]> }
      : T extends "input"
        ? unknown
        : { data: null });
}[number];

export const internalEventsDef = [
  {
    name: "plugin.start",
    dataSchema: z.object({
      isRestart: z.boolean().prefault(false),
      restartCount: z.number().prefault(0),
    }),
  },
  { name: "plugin.stop" },
  { name: "plugin.test" },
  {
    name: "plugin.error",
    dataSchema: z.object({
      error: z.custom<LifeErrorUnion>(),
      event: pluginEventSchema,
    }),
  },
] as const satisfies PluginEventsDefinition;

export type PluginInternalEvent<T extends "input" | "output"> = PluginEvent<
  typeof internalEventsDef,
  T
>;

// Handlers
export type PluginHandlerBlockFunction<
  PluginDef extends PluginDefinition,
  StateDef extends PluginHandlerStateDefinition<PluginDef>,
> = (params: {
  event: PluginEvent<PluginDef["events"], "output">;
  state: PluginHandlerState<PluginDef, StateDef>;
  plugin: op.ToPublic<
    PluginAccessor<
      PluginDef,
      { type: "handler"; plugin: string; handler: string; event: string },
      "write"
    >
  >;
  agent: op.ToPublic<AgentServer>;
  dependencies: op.ToPublic<
    PluginDependenciesAccessor<
      PluginDef["dependencies"],
      { type: "handler"; plugin: string; handler: string; event: string }
    >
  >;
  telemetry: TelemetrySpanHandle;
}) => MaybePromise<unknown>;

export type PluginHandlerStreamFunction<
  PluginDef extends PluginDefinition,
  StateDef extends PluginHandlerStateDefinition<PluginDef>,
> = (params: {
  event: PluginEvent<PluginDef["events"], "output">;
  state: PluginHandlerState<PluginDef, StateDef>;
  plugin: op.ToPublic<
    PluginAccessor<PluginDef, { type: "handler"; plugin: string; handler: string; event: string }>
  >;
  agent: op.ToPublic<AgentServer>;
  dependencies: op.ToPublic<
    PluginDependenciesAccessor<
      PluginDef["dependencies"],
      { type: "handler"; plugin: string; handler: string; event: string }
    >
  >;
  telemetry: TelemetrySpanHandle;
}) => MaybePromise<unknown>;

export type PluginHandlerInterceptFunction<
  PluginDef extends PluginDefinition,
  StateDef extends PluginHandlerStateDefinition<PluginDef>,
> = (params: {
  event: PluginEvent<PluginDef["dependencies"][number]["events"], "output">;
  state: PluginHandlerState<PluginDef, StateDef>;
  next: (
    data: PluginEvent<PluginDef["dependencies"][number]["events"], "output">["data"],
    reason: string,
  ) => void;
  drop: (reason: string) => void;
  plugin: op.ToPublic<
    PluginAccessor<PluginDef, { type: "handler"; plugin: string; handler: string; event: string }>
  >;
  dependency: op.ToPublic<
    PluginAccessor<
      PluginDef["dependencies"][number] & { dependencies: [] },
      { type: "handler"; plugin: string; handler: string; event: string }
    >
  >;
  agent: op.ToPublic<AgentServer>;
  telemetry: TelemetrySpanHandle;
}) => MaybePromise<unknown>;

export type PluginHandlerStateDefinition<PluginDef extends PluginDefinition> =
  | Record<string, unknown>
  | ((params: { config: PluginConfig<PluginDef["config"], "output"> }) => Record<string, unknown>);

export type PluginHandlerState<
  PluginDef extends PluginDefinition,
  StateDef extends PluginHandlerStateDefinition<PluginDef>,
> = StateDef extends (p: infer _) => Record<string, unknown> ? ReturnType<StateDef> : StateDef;

export type PluginHandlerDefinition<
  PluginDef extends PluginDefinition = PluginDefinition,
  Name extends string = string,
  StateDef extends PluginHandlerStateDefinition<PluginDef> = Record<string, unknown>,
> = {
  name: Name;
  state?: PluginHandlerStateDefinition<PluginDef>;
} & (
  | { mode: "block"; onEvent: PluginHandlerBlockFunction<PluginDef, StateDef> }
  | { mode: "stream"; onEvent: PluginHandlerStreamFunction<PluginDef, StateDef> }
  | { mode: "intercept"; onEvent: PluginHandlerInterceptFunction<PluginDef, StateDef> }
);

// Helper type to extract context once (prevents inlining)
export type ExtractPluginContext<PluginDef extends PluginDefinition> = PluginContext<
  PluginDef["context"],
  "output"
>;

// Accessor
export type PluginAccessor<
  PluginDef extends PluginDefinition,
  Source extends PluginEventSource,
  HandlerAccess extends "read" | "write" = "read",
> = (Source["type"] extends "client" // Exclude the config if the source is a client
  ? unknown
  : { config: PluginConfig<PluginDef["config"], "output"> }) & {
  context: {
    get(): op.OperationResult<ExtractPluginContext<PluginDef>>;
    /** Subscribe to changes in the context. Returns a function to unsubscribe. */
    onChange(
      selector: (context: ExtractPluginContext<PluginDef>) => unknown,
      callback: (
        newContext: ExtractPluginContext<PluginDef>,
        oldContext: ExtractPluginContext<PluginDef>,
      ) => void,
    ): op.OperationResult<() => void>;
    /** Returns a cloned snapshot of the context. */
  } & (HandlerAccess extends "write"
    ? {
        /** Set a value in the context. */
        set(
          valueOrUpdater:
            | ExtractPluginContext<PluginDef>
            | ((ctx: ExtractPluginContext<PluginDef>) => ExtractPluginContext<PluginDef>),
        ): op.OperationResult<void>;
      }
    : unknown);
  events: {
    emit: (
      event: Exclude<
        PluginEvent<PluginDef["events"], "input">,
        { name: (typeof internalEventsDef)[number]["name"] }
      >,
    ) => Source["type"] extends "client"
      ? Promise<op.OperationResult<string>>
      : op.OperationResult<string>;

    /**
     * Wait for the return type of a specific handler when processing a given event.
     * Note that the intercept handlers don't return a result, and so are not supported by this method.
     * @param eventId
     * @param handlerName
     * @returns
     */
    waitForProcessing: (eventId: string) => Promise<op.OperationResult<void>>;
    waitForResult: <
      HandlerName extends Exclude<PluginDef["handlers"][number], { mode: "intercept" }>["name"],
    >(
      eventId: string,
      handlerName: HandlerName,
    ) => Promise<
      op.OperationResult<
        ReturnType<Extract<PluginDef["handlers"][number], { name: HandlerName }>["onEvent"]>
      >
    >;
  } & (Source["type"] extends "handler" // Exclude the on/once methods inside handlers, redundant, encourages bad practices
    ? unknown
    : {
        on: <const Selector extends PluginEventsSelector<PluginDef["events"]>>(
          selector: Selector,
          callback: (
            event: PluginEventsSelection<PluginDef["events"], Selector>,
          ) => MaybePromise<void>,
          includeDropped?: boolean,
        ) => op.OperationResult<() => void>;
        once: <const Selector extends PluginEventsSelector<PluginDef["events"]>>(
          selector: Selector,
          callback: (
            event: PluginEventsSelection<PluginDef["events"], Selector>,
          ) => MaybePromise<void>,
          includeDropped?: boolean,
        ) => op.OperationResult<() => void>;
      });
};

export type PluginDependenciesAccessor<
  DependenciesDef extends PluginDependenciesDefinition,
  Source extends PluginEventSource,
> = {
  [DependencyDef in DependenciesDef[number] as DependencyDef["name"]]: PluginAccessor<
    DependencyDef & { dependencies: [] },
    Source
  >;
};

// - Helper type to extract event names from event definitions
export type PluginEventsSelector<EventsDef extends PluginEventsDefinition> =
  | "*"
  | EventsDef[number]["name"]
  | EventsDef[number]["name"][]
  | { include: EventsDef[number]["name"][] | "*"; exclude?: EventsDef[number]["name"][] };

export type PluginEventsSelection<
  EventsDef extends PluginEventsDefinition,
  Selector extends PluginEventsSelector<EventsDef>,
> = Selector extends "*"
  ? PluginEvent<EventsDef, "output">
  : Selector extends string
    ? Extract<PluginEvent<EventsDef, "output">, { name: Selector }>
    : Selector extends (infer S)[]
      ? S extends EventsDef[number]["name"]
        ? Extract<PluginEvent<EventsDef, "output">, { name: S }>
        : never
      : Selector extends { include: infer I; exclude?: infer E }
        ? I extends "*"
          ? E extends string[]
            ? Extract<
                PluginEvent<EventsDef, "output">,
                { name: Exclude<EventsDef[number]["name"], E[number]> }
              >
            : PluginEvent<EventsDef, "output">
          : I extends string[]
            ? E extends string[]
              ? Extract<PluginEvent<EventsDef, "output">, { name: Exclude<I[number], E[number]> }>
              : Extract<PluginEvent<EventsDef, "output">, { name: I[number] }>
            : never
        : never;

// Definition
export interface PluginDefinition {
  name: string;
  dependencies: PluginDependenciesDefinition;
  config: PluginConfigDefinition;
  context: PluginContextDefinition;
  events: PluginEventsDefinition;
  handlers: PluginHandlerDefinition[];
}

// Builder
// - Helper to return the builder without the excluded methods
export type TypedPluginBuilder<
  PluginDef extends PluginDefinition,
  Excluded extends string = never,
> = Omit<PluginBuilder<PluginDef, Excluded>, Excluded>;

// - Helper to filter out an handler by name in removeHandler() method
export type FilterHandlersByName<
  Handlers extends { name: string }[],
  Name extends string,
> = Handlers extends [{ name: infer N }, ...infer Rest extends { name: string }[]]
  ? N extends Name
    ? FilterHandlersByName<Rest, Name>
    : [Handlers[0], ...FilterHandlersByName<Rest, Name>]
  : [];

// Server
export type PluginEventsListener = {
  id: string;
  selector: PluginEventsSelector<PluginEventsDefinition>;
  callback: ((event: Any) => MaybePromise<void>) | "remote";
  includeDropped: boolean;
};

export type PluginContextListener = {
  id: string;
  selector: (context: Any) => Any;
  callback: (newContext: Any, oldContext: Any) => MaybePromise<void>;
};
